import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LayoutActionLoadError } from '@shared/store/actions/layout.action';
import { NGXLogger } from 'ngx-logger';
import { map, of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { StoreExpenseActions } from './store-expense.actions';
import {
  STORE_EXPENSE_WORKFLOW_ERROR_CODE,
  hasStoreExpenseWorkflowErrorMessage
} from '../models/store-expense-workflow-error';
import { StoreExpenseErrorCode } from '../models/store-expense.model';
import { StoreExpenseService } from '../service/store-expense.service';

@Injectable()
export class StoreExpenseEffects {
  constructor(
    private actions$: Actions,
    private logger: NGXLogger,
    private storeExpenseService: StoreExpenseService
  ) {}

  storeExpenseRemainRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoreExpenseActions.storeExpenseRemainRequest),
      tap((_) => this.logger.debug('@Effect [Store Expense] Store Expense Remain Request')),
      switchMap(() => {
        return this.storeExpenseService.getStoreExpenseRemain().pipe(
          map((response) => {
            return StoreExpenseActions.storeExpenseRemainResponse({ response });
          }),
          catchError((err) => of(new LayoutActionLoadError(err)))
        );
      })
    );
  });

  storeExpenseRequestListRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoreExpenseActions.storeExpenseRequestListRequest),
      tap((_) => this.logger.debug('@Effect [Store Expense] Store Expense Request List Request')),
      switchMap(({ payload }) => {
        return this.storeExpenseService.getStoreExpenseRequestList(payload).pipe(
          map((response) => {
            return StoreExpenseActions.storeExpenseRequestListResponse({ response });
          }),
          catchError((err) => of(new LayoutActionLoadError(err)))
        );
      })
    );
  });

  storeExpenseView$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoreExpenseActions.storeExpenseViewRequest),
      tap((_) => this.logger.debug('@Effect [Store Expense] Store Expense View Request')),
      switchMap(({ payload }) => {
        return this.storeExpenseService.getStoreExpenseView({ ...payload }).pipe(
          map((response) => {
            return StoreExpenseActions.storeExpenseViewResponse({ response });
          }),
          catchError((err) => of(new LayoutActionLoadError(err)))
        );
      })
    );
  });

  storeExpenseSubmitRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoreExpenseActions.storeExpenseSubmitRequest),
      tap((_) => this.logger.debug('@Effect [Store Expense] Store Expense Submit Request')),
      switchMap(({ payload }) => {
        return this.storeExpenseService.submitEvidence({ ...payload }).pipe(
          map((response) => {
            return StoreExpenseActions.storeExpenseSubmitResponse({ response });
          }),
          catchError((err) => this.handleSubmitError(err))
        );
      })
    );
  });

  storeExpenseSubmitSentBackRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoreExpenseActions.storeExpenseSubmitSendBackRequest),
      tap((_) => this.logger.debug('@Effect [Store Expense] Store Expense Submit Send Back Request')),
      switchMap(({ payload }) => {
        return this.storeExpenseService.submitSendBackEvidence({ ...payload }).pipe(
          map((response) => {
            return StoreExpenseActions.storeExpenseSubmitResponse({ response });
          }),
          catchError((err) => this.handleSubmitError(err))
        );
      })
    );
  });

  private handleSubmitError(err: any) {
    if (StoreExpenseErrorCode.includes(err.error?.code) && this.hasMessageToShow(err.error)) {
      return of(StoreExpenseActions.storeExpenseSubmitResponse({ response: err.error }));
    }
    return of(new LayoutActionLoadError(err));
  }

  // workflow error ที่ BE ไม่ได้ทำข้อความไทยไว้ ให้ตกไปใช้ Generic Error แทนการเปิด modal เปล่า
  private hasMessageToShow(error: any): boolean {
    return error?.code !== STORE_EXPENSE_WORKFLOW_ERROR_CODE || hasStoreExpenseWorkflowErrorMessage(error);
  }
}
